import React, { useEffect, useState } from 'react'
import { Form, Button } from 'react-bootstrap'
import { Textarea, ImageUploader } from '../../components/formComponents'
import moment from 'moment'
import t from '../../service/translate.service'
import findIndex from 'lodash/findIndex'
import pdfSvg from '../../assets/svg/pdf.svg'
import PropTypes from 'prop-types'
import resizer from 'react-image-file-resizer'
import { Alert } from 'antd'
import { Buffer } from 'buffer'
import LoadingPageOverlay from '../../components/LoadingPageOverlay'
import { appFeatureEnabled } from '../../service/appFeatures.service'

const FRESHCHAT_CONVERSATION_MARKER = 'Konversation über Freshchat'

/** Splits description into intro and conversation only when it's a Freshchat conversation (contains the marker). */
function parseDescriptionForConversation(description) {
  const text = (description ?? '').trim()
  if (!text) return { intro: '', conversation: '', isFreshchatConversation: false }
  const isFreshchatConversation = text.includes(FRESHCHAT_CONVERSATION_MARKER)
  if (!isFreshchatConversation) {
    return { intro: '', conversation: '', isFreshchatConversation: false }
  }
  const parts = text.split('---').map(s => s.trim())
  const hasMultiple = parts.length > 1
  const conversationPart = hasMultiple ? parts[parts.length - 1] : ''
  const introPart = hasMultiple ? parts.slice(0, -1).join(' — ').trim() : text
  return {
    intro: introPart,
    conversation: conversationPart,
    isFreshchatConversation: true
  }
}

/** Renders Freshchat conversation in a code-snippet style; plain paragraph otherwise. */
const ConversationSnippet = ({ description }) => {
  const { intro, conversation, isFreshchatConversation } = parseDescriptionForConversation(description)
  if (!description) return null
  if (!isFreshchatConversation) {
    return (
      <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{description}</p>
    )
  }
  // Split by newline, or by message tags when newlines are missing (e.g. [Agent]/[Kunde])
  const byNewline = conversation.split('\n').filter(Boolean)
  const hasNewlines = byNewline.length > 1 || (byNewline.length === 1 && conversation.includes('\n'))
  const lines = hasNewlines
    ? byNewline
    : conversation.split(/(?=\[Agent\]|\[Kunde\])/).map(s => s.trim()).filter(Boolean)
  if (lines.length === 0) {
    return (
      <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{description}</p>
    )
  }
  const snippetStyle = {
    margin: 0,
    padding: '12px 14px',
    background: '#f6f8fa',
    borderRadius: '6px',
    border: '1px solid #e1e4e8',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '13px',
    lineHeight: 1.5,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
  const agentStyle = { color: '#0550ae', fontWeight: 600 }
  const userStyle = { color: '#8250df', fontWeight: 600 }
  return (
    <div className="conversation-snippet">
      {intro && (
        <p style={{ whiteSpace: 'pre-line', marginBottom: '8px', marginTop: 0 }}>{intro}</p>
      )}
      <pre style={snippetStyle} aria-label={t.DAMAGE_REPORT_DESCRIPTION}>
        {lines.map((line, i) => {
          const agentMatch = line.match(/^\[Agent\]\s?(.*)/)
          const userMatch = line.match(/^\[Kunde\]\s?(.*)/)
          if (agentMatch) {
            return (
              <React.Fragment key={i}>
                <span style={agentStyle}>[Agent]</span> {agentMatch[1]}
                {'\n'}
              </React.Fragment>
            )
          }
          if (userMatch) {
            return (
              <React.Fragment key={i}>
                <span style={userStyle}>[Kunde]</span> {userMatch[1]}
                {'\n'}
              </React.Fragment>
            )
          }
          return <React.Fragment key={i}>{line}{'\n'}</React.Fragment>
        })}
      </pre>
    </div>
  )
}
ConversationSnippet.propTypes = { description: PropTypes.string }

const RepairsAndInquiriesContent = ({
  addFile,
  files,
  item: providedItem,
  amendment,
  submitAmendment,
  onAmendInput,
  toggleAmendForm,
  showAmendForm,
  showSuccessMessage,
  validators,
  statuses,
  selectedItem,
  error,
  excludedFiles,
  getAttachments,
  getTicketById
}) => {
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    (async () => {
      let ticket
      try {
        ticket = await getTicketById(providedItem.code)
        if (providedItem.code === selectedItem.code) {
          setLoading(true)
          const attachments = []
          const res = await getAttachments(providedItem.code)
          res.forEach((file) => {
            const fileType = file.file_name.split('.').pop()
            const fileFromBuffer = {
              file: new File([Buffer.from(file.att_datas, 'base64')], `${file.file_name}`, {
                type: fileType === 'pdf' ? 'application/pdf' : fileType === 'svg' ? 'image/svg+xml' : fileType,
              }),
              customProperties: {
                uploading: file?.uploading
              }
            }
            attachments.push(fileFromBuffer)
          })
          setItem({
            ...providedItem,
            description: ticket.description || providedItem.description,
            user_comment: ticket.user_comment || providedItem.user_comment,
            zFeedbackTenantAppKTU: ticket.zFeedbackTenantAppKTU || providedItem.zFeedbackTenantAppKTU,
            damageReportAttachments: attachments
          })
          setLoading(false)
        }
      } catch (err) {
        setItem({
          ...providedItem,
            description: ticket.description || providedItem.description,
            user_comment: ticket.user_comment || providedItem.user_comment,
            zFeedbackTenantAppKTU: ticket.zFeedbackTenantAppKTU || providedItem.zFeedbackTenantAppKTU
        })
        setLoading(false)
      }
    })()
  }, [selectedItem])

  function getDescription(item) {
    const desc = item.description;
    try {
      const parsed = JSON.parse(desc);

      if (parsed && typeof parsed === 'object' && 'description' in parsed) {
        return parsed.description;
      }
    } catch (e) {
      return desc;
    }

    return desc;
  }

  if (loading) return <LoadingPageOverlay />
  if (!item || !providedItem) return <div />

  return (
    <div className='content'>
      <div>
        <div className='contract-details'>
          <p>{item.contract.contractNumber}</p>
          <p>{item.contract.address}</p>
        </div>
      </div>
      <div>
        <div className='custom-progress'>
          {statuses.map((status, index) => {
            const currentStatusIndex = findIndex(statuses, item.repairAndInquiryStatus)
            const statusClass = index < currentStatusIndex ? '' : index === currentStatusIndex ? status.name.toLowerCase().replace(' ', '-') : 'disabled'
            const statusDate = status.id === 2 ? item.inProgressAt : status.id === 3 ? item.resolvedAt : item.created_at
            return (
              <div key={index} className={`item ${statusClass}`}>
                <div className='container'>
                  <div className='step'>{status.id}</div>
                  <div className='label'>{localStorage.getItem('locale') === 'en' ? status.name : status.nameDe}</div>
                  <div className='date'>{statusDate && moment(statusDate).format('D.M.Y')}</div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div>
        <div className='details'>
          <div className='label'>
            <p>{t.YOUR_INQUIRY}</p>
            {item.urgency === 'Urgent' && (
              <div className='urgent'>{item.urgency === 'Urgent' ? t.URGENT : ''}</div>
            )}
          </div>
          <p className='label'>{t.DAMAGE_REPORT_SUBJECT}</p>
          <p>{item.subject}</p>
          <p className='label'>{t.DAMAGE_REPORT_DESCRIPTION}</p>
          <ConversationSnippet description={getDescription(item) ?? ''} />
        </div>
      </div>
      {!!item?.damageReportAttachments?.length && (<RenderAttachment attachments={item.damageReportAttachments} />)}
      <div>
        {item.zFeedbackTenantAppKTU && (<div>
          <div className='feedbackContainer'>
            <p className='feedback'>{t.FEEDBACK_TITLE}</p>
            <p className='feedbackText'>{item.zFeedbackTenantAppKTU}</p>
          </div>
        </div>)
        }
        {showAmendForm ? (
          <AmendForm
            amendment={amendment}
            addFile={addFile}
            files={files}
            item={item}
            submitAmendment={submitAmendment}
            onAmendInput={onAmendInput}
            toggleAmendForm={toggleAmendForm}
            error={error}
            validators={validators} />
        ) : (
          <div>
            <div className='amended'>
              {showSuccessMessage && (<div className='notification confirmation'>
                <div className='content'>
                  <div className='message'>
                    {t.AMEND_SUCCESS}
                  </div>
                </div>
              </div>)}
              {excludedFiles?.length > 0 && (
                <div className='notification errorC'>
                  <div className='content'>
                    <div className='message'>
                      <p>{t.AMEND_EXCLUDED_FILES}</p>
                      <div>
                        {excludedFiles?.map((file) => {
                          return (
                            <p>{file}</p>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {item.user_comment.map((amendment, index) => {
                return (
                  <div key={`${amendment.sr_number}-${index}`}>
                    <div className='label'>
                      {t.AMENDED_ON + ' '} {moment(amendment.date).format('D.M.Y')}
                    </div>
                    <p>{amendment?.comment?.split('CreatedBy')[0]}</p>
                  </div>
                )
              })
              }
            </div>
            <div className='text-center'>
              {item.code && item?.repairAndInquiryStatus?.id != 3 && (
                <a
                  href=''
                  className='button tertiary'
                  onClick={e => {
                    if (e.key !== 'Tab') {
                      e.preventDefault()
                    }
                    toggleAmendForm()
                  }}
                  style={{
                    textDecoration: 'none',
                    color: 'unset'
                  }}
                >
                  {t.AMEND}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const RenderAttachment = ({ attachments }) => {

  const downloadFile = (file) => {
    const url = window.URL.createObjectURL(file)
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.setAttribute('style', 'display: none;')
    a.href = url
    a.download = file?.name
    a.click()
    window.URL.revokeObjectURL(url)
  }
  console.log(attachments)
  return (
    <div>
      <div className='upload-preview'>
        {attachments.map((file, index) => {
          console.log(file)
          return (
            <div key={index} className='item'>
              <div className='container'>
                <div className='box'>
                  <a
                    href=''
                    className='image'
                    onClick={(e) => {
                      if (e.key !== 'Tab') {
                        e.preventDefault()
                      }
                      downloadFile(file.file)
                    }}
                    style={{
                      textDecoration: 'none',
                      color: 'unset'
                    }}
                  >
                    <img src={file.file.type === 'application/pdf' ? pdfSvg : URL.createObjectURL(file.file)} />
                  </a>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
RenderAttachment.propTypes = {
  attachments: PropTypes.object
}

const AmendForm = ({ addFile, files, item, submitAmendment, onAmendInput, toggleAmendForm, validators, error, amendment }) => {
  const resizeFile = (file) =>
    new Promise((resolve) => {
      resizer.imageFileResizer(file, 1600, 1600, 'jpeg', 90, 0,
        (uri) => {
          resolve(uri)
        },
        'file'
      )
    })
  const onImageDrop = async (files) => {
    console.log("onImageDrop files", files)
    const resizedImg = files.map(async (file) => {
      console.log("onImageDrop", file)
      if (file.type.includes('image/')) {
        const image = await resizeFile(file)
        image.preview = file.preview
        return image
      }

      return file
    })
    console.log("resizedImg", resizedImg)
    const resized = await Promise.all(resizedImg)
    console.log(resized)
    addFile(resized)
  }
  console.log("AmendForm files", files)
  return (
    <div>
      {error && <Alert message={error} type='error' style={{marginTop: '12px', marginBottom: '12px'}} aria-live="assertive" role='alert' />}
      <Form>
          <Textarea
            label={t.AMEND_LABEL}
            placeholder={t.AMEND_PLACEHOLDER}
            value={amendment}
            onChange={e => onAmendInput(e.currentTarget.value)}
            validate={validators.message}
            required={true}
          />
        {appFeatureEnabled('srAmendmentImagesEnabled') && (
            <ImageUploader label={t['DAMAGE_REPORT_UPLOAD_IMAGE']} files={files} onImageDrop={onImageDrop} />
        )}
        <Form.Group className='actions text-center'>
          <div className='group'>
            <a
              href=''
              className='button primary'
              onClick={(e) => {
                if (e.key !== 'Tab') {
                  e.preventDefault()
                }
                submitAmendment(e, item.code)
              }}
              style={{
                textDecoration: 'none',
                color: 'unset'
              }}
            >
              {t.AMEND}
            </a>
            <a
              href=''
              className='button secondary'
              onClick={e => {
                if (e.key !== 'Tab') {
                  e.preventDefault()
                }
                toggleAmendForm()
              }}
              style={{
                textDecoration: 'none',
                color: 'unset'
              }}
            >
              {t.AMEND_CANCEL}
            </a>
          </div>
        </Form.Group>
      </Form>
    </div>
  )
}
AmendForm.propTypes = {
  addFile: PropTypes.func,
  files: PropTypes.array,
  item: PropTypes.object,
  amendment: PropTypes.string,
  submitAmendment: PropTypes.func,
  excludedFiles: PropTypes.array,
  onAmendInput: PropTypes.func,
  toggleAmendForm: PropTypes.func,
  validators: PropTypes.object
}
RepairsAndInquiriesContent.propTypes = {
  addFile: PropTypes.func,
  files: PropTypes.array,
  item: PropTypes.object,
  amendment: PropTypes.string,
  submitAmendment: PropTypes.func,
  onAmendInput: PropTypes.func,
  toggleAmendForm: PropTypes.func,
  error: PropTypes.string,
  showAmendForm: PropTypes.bool,
  showSuccessMessage: PropTypes.bool,
  statuses: PropTypes.array,
  validators: PropTypes.object
}

export default RepairsAndInquiriesContent
